import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Routes from 'lib/utils/routes';
import { throwErrorToast, throwSuccessToast } from 'lib/utils/toast';
import { createActivitySetInput } from 'lib/utils/activities';
import { TEMP_REFS } from 'lib/constants';

import { CREATE_ACTIVITY_SET_WITH_WORK_ORDER } from 'lib/api/mutations';
import { GET_RECENT_OPERATOR_RUNS } from 'lib/api/queries';
import {
  actionCanonicalActivitySetOpen,
  actionCanonicalActivitySetDelete,
  actionOptimisticActivitySetOpen,
  actionSetJobTotal,
  actionSetWorkOrderOpActionLoading,
  actionSetPendingActivityTypeRef,
  actionSetRecentOperatorRuns,
} from 'lib/actions';
import { getLatestJob } from 'lib/selectors/getLatestJob';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { useIsLaborTicketFlow } from 'lib/hooks/useIsLaborTicketFlow';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getHasActiveOperatorRun } from 'lib/selectors/getHasActiveOperatorRun';
import { bugsnag } from 'lib/external/bugsnag';
import useStartActivityWithLaborTicket from './useStartActivityWithLaborTicket';

const {
  ACTIVITY_REF: optimisticActivityRef,
  ACTIVITY_SET_REF: optimisticActivitySetRef,
} = TEMP_REFS;

const useCreateActivitySetWithWorkOrder = ({ machine, workOrderOp }) => {
  const mainDispatch = useDispatch();
  const history = useHistory();
  const laborTicketEnabled = useIsLaborTicketFlow();
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);
  const isAPMEnabled = useSelector(getIsAPMEnabled);
  const currentOperator = useSelector(getCurrentOperator);
  const hasActiveOperatorRun = useSelector(getHasActiveOperatorRun);

  const workOrderId = workOrderOp?.workOrderId;

  const job = useSelector(getLatestJob);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const laborTicketRequired = isJobLaborTrackingEnabled && !isAPMEnabled;

  const requestStartActivityWithLaborTicket = useStartActivityWithLaborTicket({
    machine,
    workOrderOp,
  });

  const [requestRecentOperatorRuns] = useLazyQuery(GET_RECENT_OPERATOR_RUNS, {
    fetchPolicy: 'no-cache',
  });

  const [requestCreateActivitySetWithWorkOrder] = useMutation(
    CREATE_ACTIVITY_SET_WITH_WORK_ORDER,
    {
      fetchPolicy: 'no-cache',
      update: (
        _cache,
        {
          data: {
            createActivitySetWithWorkOrderResponse: {
              activitySet: {
                activities: [activity],
                ...activitySet
              },
            },
          },
        }
      ) => {
        mainDispatch(
          actionOptimisticActivitySetOpen({ activitySet, activity, job })
        );
        mainDispatch(actionSetJobTotal(undefined));
        history.push(Routes.machineIdPath(machine.id));
      },
      onCompleted: async ({
        createActivitySetWithWorkOrderResponse: {
          activitySet: {
            activities: [activity], // should be sorted start ASC
            ...activitySet
          },
        },
      }) => {
        await mainDispatch(
          actionCanonicalActivitySetOpen({
            activitySet,
            activity,
            optimisticActivitySetRef,
            optimisticActivityRef,
          })
        );

        throwSuccessToast(`Started order ${activitySet.workOrderId}`);
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
        history.push(Routes.machineIdPath(machine.id));
      },
      onError: () => {
        mainDispatch(
          actionCanonicalActivitySetDelete({
            activitySetRef: optimisticActivitySetRef,
          })
        );
        mainDispatch(actionSetWorkOrderOpActionLoading(false));
        mainDispatch(actionSetPendingActivityTypeRef(null));
        throwErrorToast('Could not start production order');
      },
    }
  );

  return async ({
    activityTypeRef,
    partOperation,
    startAt,
    workOrderOp: callTimeWorkOrderOp,
  }) => {
    // Use call-time workOrderOp if provided, otherwise fall back to initialization value
    const effectiveWorkOrderOp = callTimeWorkOrderOp || workOrderOp;
    const effectiveWorkOrderId =
      effectiveWorkOrderOp?.workOrderId || workOrderId;

    if (partOperation && laborTicketEnabled && effectiveWorkOrderOp) {
      await requestStartActivityWithLaborTicket({
        activityTypeRef,
        partOperation,
        startAt,
      });
    } else if (partOperation && laborTicketRequired && effectiveWorkOrderOp) {
      bugsnag.notify(
        new Error('Blocked activity set creation without labor ticket'),
        (event) => {
          event.addMetadata('laborTicketFlow', {
            machineId: machine?.id,
            machineRef: machine?.machineRef,
            workOrderId: effectiveWorkOrderId,
            workOrderOperationRef: effectiveWorkOrderOp?.workOrderOperationRef,
            activityTypeRef,
            partOperationRef: partOperation?.partOperationRef,
            isJobLaborTrackingEnabled,
            isAPMEnabled,
            hasActiveOperatorRun,
            operatorId: currentOperator?.id,
            operatorHasErpId: !!currentOperator?.erpId,
          });
        }
      );

      try {
        const { data } = machine?.machineRef
          ? await Promise.resolve(
              requestRecentOperatorRuns({
                variables: {
                  machineRef: machine.machineRef,
                  limit: 1,
                },
              })
            )
          : {};

        if (data?.operatorRuns) {
          mainDispatch(actionSetRecentOperatorRuns(data.operatorRuns));
        }
      } catch (error) {
        bugsnag.notify(error, (event) => {
          event.addMetadata('laborTicketFlow', {
            machineId: machine?.id,
            machineRef: machine?.machineRef,
            workOrderId: effectiveWorkOrderId,
            refreshRecentOperatorRunsFailed: true,
          });
        });
      }

      mainDispatch(actionSetWorkOrderOpActionLoading(false));
      mainDispatch(actionSetPendingActivityTypeRef(null));
      throwErrorToast('Could not start production order, please try again');
    } else if (partOperation) {
      const activitySetInput = createActivitySetInput({
        machine,
        workOrderOp: effectiveWorkOrderOp,
        partOperation,
        activityTypeRef,
        startAt,
      });

      await requestCreateActivitySetWithWorkOrder({
        variables: {
          input: activitySetInput,
          query: { shouldCloseLatestSet: true },
        },

        optimisticResponse: {
          __typename: 'Mutation',
          createActivitySetWithWorkOrderResponse: {
            __typename: 'ActivitySet',
            activitySet: {
              ...latestActivitySet,
              workOrderId: effectiveWorkOrderId,
            },
          },
        },
      });
    }
  };
};

export default useCreateActivitySetWithWorkOrder;
